/**
 * PrefillPromptShell — visual chrome for the "subir documento para
 * auto-completar" affordance. Used by every PM phase (CompanyData,
 * Representante, Apoderado, Beneficiarios) so the OCR pre-fill feature
 * — the thing Pro users pay for — gets consistent, prominent treatment
 * across the wizard.
 *
 * Design intent
 * ─────────────
 *  IDLE / EXTRACTING / FAILED  →  Large, gradient card. Sparkles + Upload
 *                                 icon, headline, subtitle, the actual
 *                                 uploader (children), and a clear
 *                                 SECONDARY button for "capturar manualmente"
 *                                 (NOT a small underlined text link).
 *  DONE (collapsed=true)        →  Compact one-line confirmation strip with
 *                                 a "Reemplazar" button — never a dropzone-
 *                                 sized empty card after success.
 *
 *  Plans without OCR never see this component (the caller short-circuits
 *  to the manual form). The "available on Pro" hint is rendered by the
 *  caller separately.
 *
 * Props
 *  - title          : headline (e.g. "Sube la CSF y autocompletamos…")
 *  - subtitle       : one-line elaboration shown under the title
 *  - state          : "idle" | "extracting" | "partial" | "done" | "failed"
 *  - elapsedSec     : seconds; rendered next to the spinner when extracting
 *  - manualLabel    : button copy for switching to manual capture
 *  - onManual       : called when the user clicks the manual button
 *  - onReplace      : called when the user clicks Reemplazar in the done state
 *  - children       : the actual uploader element (SubjectDocUploader, etc.)
 *  - testIdPrefix   : prefix for data-testids so multiple shells on the
 *                     same page (e.g. multiple BO cards) stay unique
 */
import React from "react";
import { Sparkles, Loader2, CheckCircle2, AlertCircle, FilePlus, Pencil } from "lucide-react";
import { Button } from "../ui/button";

export default function PrefillPromptShell({
  title,
  subtitle,
  state = "idle",
  elapsedSec = 0,
  manualLabel = "Prefiero capturar a mano",
  onManual,
  onReplace,
  // Multi-document phases (REP/APO/BO) want the prominent card to
  // STAY VISIBLE even after the first OCR completes, because the
  // user may still need to add the CSF or comprobante. Pass
  // ``collapseOnDone={false}`` to disable the compact "done" strip.
  collapseOnDone = true,
  children,
  testIdPrefix = "prefill",
}) {
  // Collapse to the compact "done" strip once extraction finished
  // successfully — empty dropzones after success would feel broken.
  if (state === "done" && collapseOnDone) {
    return (
      <div
        data-testid={`${testIdPrefix}-done`}
        className="flex items-center gap-3 rounded-xl border border-teal-700/40 bg-teal-950/30 px-4 py-3"
      >
        <CheckCircle2 className="w-5 h-5 text-teal-400 shrink-0" />
        <div className="flex-1 min-w-0 text-sm text-slate-100">
          <span className="font-medium">Documentos cargados</span>
          <span className="text-slate-300"> — datos pre-llenados desde la lectura automática.</span>
        </div>
        {onReplace && (
          <Button
            variant="outline"
            size="sm"
            data-testid={`${testIdPrefix}-replace-btn`}
            onClick={onReplace}
            className="shrink-0"
          >
            <FilePlus className="w-3.5 h-3.5 mr-1.5" /> Reemplazar
          </Button>
        )}
      </div>
    );
  }

  // Idle / extracting / partial / failed — the prominent card.
  return (
    <div
      data-testid={`${testIdPrefix}-card`}
      className={[
        "relative overflow-hidden rounded-2xl border p-5",
        "bg-gradient-to-br from-teal-950/40 via-slate-900/40 to-slate-900/20",
        state === "failed"
          ? "border-amber-700/50"
          : "border-teal-700/50 shadow-[0_0_0_1px_rgba(45,212,191,0.08)]",
      ].join(" ")}
    >
      {/* Decorative grain so the card looks "premium" rather than a
          generic dropzone — gradients alone tend to read as AI-slop. */}
      <div
        aria-hidden
        className="pointer-events-none absolute inset-0 opacity-[0.07]"
        style={{
          backgroundImage:
            "radial-gradient(circle at 20% 0%, rgba(45,212,191,0.6) 0%, transparent 40%), radial-gradient(circle at 100% 100%, rgba(125,211,252,0.4) 0%, transparent 50%)",
        }}
      />

      <div className="relative flex items-start gap-3">
        <div className="rounded-xl bg-teal-500/10 border border-teal-500/30 p-2 shrink-0">
          <Sparkles className="w-5 h-5 text-teal-300" />
        </div>
        <div className="flex-1 min-w-0">
          <h3
            data-testid={`${testIdPrefix}-title`}
            className="text-base font-semibold text-white"
          >
            {title}
          </h3>
          {subtitle && (
            <p className="text-sm text-slate-300/90 mt-1">{subtitle}</p>
          )}

          {/* The uploader itself */}
          <div className="mt-4">
            {children}
          </div>

          {/* Status line — extracting / failed / partial */}
          <div className="mt-3">
            {state === "extracting" && (
              <div
                data-testid={`${testIdPrefix}-extracting`}
                className="flex items-center gap-1.5 text-sm text-teal-300"
              >
                <Loader2 className="w-4 h-4 animate-spin" />
                Extrayendo datos…
                {elapsedSec > 0 && (
                  <span className="text-teal-300/70">({elapsedSec}s)</span>
                )}
              </div>
            )}
            {state === "failed" && (
              <div
                data-testid={`${testIdPrefix}-failed`}
                className="flex items-center gap-1.5 text-sm text-amber-300"
              >
                <AlertCircle className="w-4 h-4" />
                No pudimos extraer algunos campos. Completa lo que falte manualmente.
              </div>
            )}
            {state === "partial" && (
              <div
                data-testid={`${testIdPrefix}-partial`}
                className="flex items-center gap-1.5 text-sm text-slate-300"
              >
                <Sparkles className="w-4 h-4 text-teal-300" />
                Sigue subiendo documentos para enriquecer el pre-llenado.
              </div>
            )}
            {state === "done" && !collapseOnDone && (
              <div
                data-testid={`${testIdPrefix}-inline-done`}
                className="flex items-center gap-1.5 text-sm text-teal-300"
              >
                <CheckCircle2 className="w-4 h-4" />
                Datos pre-llenados — sigue subiendo documentos para enriquecer.
              </div>
            )}
          </div>

          {/* Secondary affordance for manual capture. A real button,
              not a small underlined link — the user said: NO tenue. */}
          {onManual && (
            <div className="mt-4 pt-3 border-t border-teal-700/20">
              <Button
                variant="ghost"
                size="sm"
                data-testid={`${testIdPrefix}-manual-btn`}
                onClick={onManual}
                className="text-slate-300 hover:text-white"
              >
                <Pencil className="w-3.5 h-3.5 mr-1.5" /> {manualLabel}
              </Button>
            </div>
          )}
        </div>
      </div>
    </div>
  );
}


/**
 * PrefillReturnBanner — when the user IS on a Pro plan and chose
 * manual capture, surface a clear way back to the OCR path. Lives
 * next to the manual form (NOT inside the form), as a secondary
 * affordance — visible but not co-protagonist.
 *
 * Distinct visual treatment vs the prominent IDLE card above: a
 * single button-row strip, no gradient, no sparkles glow. The point
 * is "you can come back to auto" without making the user feel they
 * made the wrong choice.
 */
export function PrefillReturnBanner({ onReturn, label, testId = "prefill-return" }) {
  return (
    <div
      data-testid={testId}
      className="flex items-center justify-between gap-3 rounded-lg border border-teal-700/30 bg-slate-900/50 px-4 py-3"
    >
      <div className="flex items-center gap-2 text-sm text-slate-300">
        <Sparkles className="w-4 h-4 text-teal-400" />
        {label}
      </div>
      <Button
        variant="outline"
        size="sm"
        data-testid={`${testId}-btn`}
        onClick={onReturn}
        className="shrink-0"
      >
        <FilePlus className="w-3.5 h-3.5 mr-1.5" /> Usar pre-llenado automático
      </Button>
    </div>
  );
}


/**
 * OcrUpsellHint — the "available on Pro" notice for plans without OCR.
 * Kept here so PhaseCompanyData / REP / APO / BO render the same hint
 * shape; previously each phase had a slightly different inline string.
 */
export function OcrUpsellHint({ text, testId = "ocr-disabled-hint" }) {
  return (
    <div
      data-testid={testId}
      className="rounded-lg border border-slate-700 bg-slate-900/40 p-3 flex items-start gap-2"
    >
      <Sparkles className="w-4 h-4 text-teal-400/60 shrink-0 mt-0.5" />
      <div className="text-xs text-slate-400">
        {text}
        <span className="text-teal-300 font-medium"> Pro</span>.
      </div>
    </div>
  );
}
